import { ref, push, update, remove, onValue, off } from 'firebase/database';
import { database } from '../firebase';
import { getUserIdFromUserWithLegacySupport } from '../utils/customUserId';

export const createSnippet = async (user, snippetData) => {
  try {
    console.log('🔄 Creating snippet for user:', user.email);
    console.log('📊 Snippet data:', snippetData);
    
    // Use custom user ID based on email prefix
    const customUserId = getUserIdFromUserWithLegacySupport(user);
    console.log('🆔 Using custom user ID:', customUserId);
    
    const snippetsRef = ref(database, `users/${customUserId}/snippets`);
    console.log('📍 Database path:', `users/${customUserId}/snippets`);
    
    const newSnippetRef = push(snippetsRef);
    
    const snippet = {
      ...snippetData,
      id: newSnippetRef.key,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      usageCount: 0
    };
    
    console.log('💾 Writing snippet to Firebase:', snippet);
    await update(newSnippetRef, snippet);
    
    console.log(`✅ Created snippet for user ${user.email} (ID: ${customUserId})`);
    return { id: newSnippetRef.key, ...snippet };
  } catch (error) {
    console.error('❌ Error creating snippet:', error);
    console.error('❌ Error details:', {
      code: error.code,
      message: error.message,
      customUserId: getUserIdFromUserWithLegacySupport(user)
    });
    throw error;
  }
};

export const updateSnippet = async (user, snippetId, updates) => {
  try {
    // Use custom user ID based on email prefix
    const customUserId = getUserIdFromUserWithLegacySupport(user);
    const snippetRef = ref(database, `users/${customUserId}/snippets/${snippetId}`);
    const updatedData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    await update(snippetRef, updatedData);
    console.log(`Updated snippet for user ${user.email} (ID: ${customUserId})`);
    return updatedData;
  } catch (error) {
    console.error('Error updating snippet:', error);
    throw error;
  }
};

export const deleteSnippet = async (user, snippetId) => {
  try {
    // Use custom user ID based on email prefix
    const customUserId = getUserIdFromUserWithLegacySupport(user);
    const snippetRef = ref(database, `users/${customUserId}/snippets/${snippetId}`);
    await remove(snippetRef);
    console.log(`Deleted snippet for user ${user.email} (ID: ${customUserId})`);
  } catch (error) {
    console.error('Error deleting snippet:', error);
    throw error;
  }
};

export const subscribeToSnippets = (user, callback) => {
  // Use custom user ID based on email prefix
  const customUserId = getUserIdFromUserWithLegacySupport(user);
  const snippetsRef = ref(database, `users/${customUserId}/snippets`);
  
  console.log(`Subscribing to snippets for user ${user.email} (ID: ${customUserId})`);
  
  // Add timeout to prevent infinite loading
  const timeoutId = setTimeout(() => {
    console.warn('Firebase connection timeout - using offline mode');
    callback([]);
  }, 10000); // 10 second timeout
  
  const unsubscribe = onValue(snippetsRef, (snapshot) => {
    clearTimeout(timeoutId);
    const data = snapshot.val();
    const snippets = data ? Object.entries(data).map(([id, snippet]) => ({
      id,
      ...snippet
    })) : [];
    console.log(`Loaded ${snippets.length} snippets for user ${user.email} (ID: ${customUserId})`);
    callback(snippets);
  }, (error) => {
    clearTimeout(timeoutId);
    console.error('Error fetching snippets:', error);
    callback([]);
  });
  
  return () => {
    clearTimeout(timeoutId);
    off(snippetsRef, 'value', unsubscribe);
  };
};

export const updateUserSettings = async (user, settings) => {
  try {
    // Use custom user ID based on email prefix
    const customUserId = getUserIdFromUserWithLegacySupport(user);
    const settingsRef = ref(database, `users/${customUserId}/settings`);
    await update(settingsRef, {
      ...settings,
      updatedAt: new Date().toISOString()
    });
    console.log(`Updated settings for user ${user.email} (ID: ${customUserId})`);
  } catch (error) {
    console.error('Error updating user settings:', error);
    throw error;
  }
};

export const getUserSettings = async (user, callback) => {
  // Use custom user ID based on email prefix
  const customUserId = getUserIdFromUserWithLegacySupport(user);
  const settingsRef = ref(database, `users/${customUserId}/settings`);
  
  console.log(`Loading settings for user ${user.email} (ID: ${customUserId})`);
  
  const unsubscribe = onValue(settingsRef, (snapshot) => {
    const data = snapshot.val();
    callback(data || {
      defaultTypingSpeed: 80,
      randomVariation: 30,
      enableBackspaces: true,
      theme: 'light'
    });
  });
  
  return () => off(settingsRef, 'value', unsubscribe);
};

export const initializeUserProfile = async (user, userProfile) => {
  try {
    // Use custom user ID based on email prefix
    const customUserId = getUserIdFromUserWithLegacySupport(user);
    const profileRef = ref(database, `users/${customUserId}/profile`);
    await update(profileRef, {
      ...userProfile,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    console.log(`Initialized profile for user ${user.email} (ID: ${customUserId})`);
  } catch (error) {
    console.error('Error initializing user profile:', error);
    throw error;
  }
};
